// src/auth/authStorage.js
// Central auth/cache storage layer for Loop.
// Purpose:
// - Keep all user-specific cache keys scoped by uid/email.
// - Clear every auth-scoped cache on logout / user switch.
// - Preserve registration pending keys only when explicitly requested.

export const LOOP_NATIVE_AUTH_RESTORE_KEY = "loop_native_auth_restore_allowed_v1";

const AUTH_EXACT_KEYS = [
  "__MANUAL_AUTH__",
  "loop_manual_auth",
  "loop_auth_user",
  "loop_current_user",
  "loop_profile_current_user",
  "loop_cached_user",
  "loop_session_user",
  "loop_worker_businesses_modal_cache_v2",
  "loop_worker_businesses_modal_cache_ts_v2",
  "loop_booking_toast_seen_v1",
  "loop_notification_center_filters_v2",
  "loop_push_registration_state_v1",
  "loop_fcm_token_v1",
  "loop_web_fcm_token_v1",
  LOOP_NATIVE_AUTH_RESTORE_KEY,
];

const AUTH_PREFIXES = [
  "loop_profile_fast_cache_v1:",
  "loop_user_businesses_roles_v3:",
  "loop:businessAccess:",
  "loop_mainmenu_favorites_docs_v1",
  "loop_mainmenu_calendar_access_v2",
  "loop_worker_businesses_modal_cache_v3:",
  "loop_booking_toast_seen_v1:",
  "loop_notification_center_filters_v2:",
  "loop_push_registration_state_v1:",
  "firebase:authUser:",
];

// Public, non-auth-scoped keys that should survive logout.
const PRESERVE_EXACT_KEYS = new Set([
  "loop_search_location_v1",
  "loop_platform_analytics_visitor_id_v1",
]);

function normalizeEmail(value) {
  return String(value || "").trim().toLowerCase();
}

export function identityFromUserLike(userLike = {}) {
  const source = userLike && typeof userLike === "object" ? userLike : {};
  const user = source.user && typeof source.user === "object" ? source.user : source;
  return {
    uid: user?.uid ? String(user.uid).trim() : "",
    email: normalizeEmail(user?.email || user?.contactEmail || user?.authEmail || ""),
  };
}

export function getIdentityKey(userLike = {}) {
  const { uid, email } = identityFromUserLike(userLike);
  if (uid) return `uid:${uid}`;
  if (email) return `email:${email}`;
  return "guest";
}

export function makeUserScopedStorageKey(baseKey, userLike = {}) {
  const base = String(baseKey || "").trim();
  if (!base) return "";
  return `${base}:${getIdentityKey(userLike)}`;
}

export function readJsonStorage(key, fallback = null) {
  try {
    if (typeof localStorage === "undefined" || !key) return fallback;
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    return parsed == null ? fallback : parsed;
  } catch (_) {
    return fallback;
  }
}

export function writeJsonStorage(key, value) {
  try {
    if (typeof localStorage === "undefined" || !key) return;
    localStorage.setItem(key, JSON.stringify(value));
  } catch (_) {}
}

export function removeStorageKey(key) {
  try {
    if (typeof localStorage !== "undefined" && key) localStorage.removeItem(key);
  } catch (_) {}
}

export function readScopedJsonStorage(baseKey, userLike, fallback = null) {
  const key = makeUserScopedStorageKey(baseKey, userLike);
  return readJsonStorage(key, fallback);
}

export function writeScopedJsonStorage(baseKey, userLike, value) {
  const key = makeUserScopedStorageKey(baseKey, userLike);
  writeJsonStorage(key, value);
}

export function removeScopedJsonStorage(baseKey, userLike) {
  removeStorageKey(makeUserScopedStorageKey(baseKey, userLike));
}

export function listStorageKeys(storage) {
  const out = [];
  try {
    if (!storage) return out;
    for (let i = 0; i < storage.length; i += 1) {
      const key = storage.key(i);
      if (key) out.push(key);
    }
  } catch (_) {}
  return out;
}

function shouldRemoveLocalKey(key, keepPendingRegistration) {
  const k = String(key || "");
  if (!k) return false;
  if (PRESERVE_EXACT_KEYS.has(k)) return false;
  if (keepPendingRegistration && (k === "pending_uid" || k === "pending_email")) return false;
  if (!keepPendingRegistration && (k === "pending_uid" || k === "pending_email")) return true;
  if (AUTH_EXACT_KEYS.includes(k)) return true;
  if (AUTH_PREFIXES.some((prefix) => k.startsWith(prefix))) return true;
  return false;
}

export function clearAuthScopedStorage(options = {}) {
  const keepPendingRegistration = options.keepPendingRegistration === true;
  const removed = [];

  try {
    if (typeof localStorage !== "undefined") {
      const keys = listStorageKeys(localStorage);
      keys.forEach((key) => {
        if (!shouldRemoveLocalKey(key, keepPendingRegistration)) return;
        try {
          localStorage.removeItem(key);
          removed.push(key);
        } catch (_) {}
      });
    }
  } catch (_) {}

  try {
    if (typeof sessionStorage !== "undefined") {
      const keys = listStorageKeys(sessionStorage);
      keys.forEach((key) => {
        const k = String(key || "");
        if (keepPendingRegistration && [
          'loop_business_guest_handoff', 'loop_business_resume_step',
          'loop_business_campaign_v1', 'loop_business_funnel_session',
        ].includes(k)) return;
        if (k.startsWith("loop_") || k.startsWith("intent_")) {
          try { sessionStorage.removeItem(k); removed.push(`session:${k}`); } catch (_) {}
        }
      });
    }
  } catch (_) {}

  return removed;
}

export function emitStorageScopeChanged(detail = {}) {
  try {
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("loop-auth-storage-cleared", { detail }));
    }
  } catch (_) {}
}
